import { NoddiIconName, type VariantProps } from 'noddi-ui-common';
import { NoddiButton, noddiButtonVariants } from '../../../components/Inputs/NoddiButton';
import { useFormContext } from '../formContext';

type Props = {
  label: string;
  className?: string;
  disabled?: boolean;
  /** Why submitting is unavailable, shown under the button. See `NoddiButton`. */
  disabledReason?: string;
  /** The id of a reason rendered elsewhere, e.g. under a dialog's button row where `disabledReason` would shift the buttons. */
  'aria-describedby'?: string;
  formId?: string;
  /**
   * Pressing leaves focus in the field that had it, so a phone keeps its keyboard open and hands it to
   * the field the next step focuses, e.g. send an SMS code, then type it. A phone opens the keyboard
   * for a programmatic focus only when focus moves there from a field that already holds it.
   */
  keepsFocus?: boolean;
} & VariantProps<typeof noddiButtonVariants> &
  // Mirrors NoddiButton: one icon side, so a Continue can point forward.
  ({ startIcon?: NoddiIconName; endIcon?: never } | { endIcon?: NoddiIconName; startIcon?: never });

export const TanstackSubscribeButton = ({
  label,
  className,
  disabled,
  disabledReason,
  variant,
  size,
  startIcon,
  endIcon,
  formId,
  keepsFocus,
  'aria-describedby': ariaDescribedBy
}: Props) => {
  const form = useFormContext();

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <NoddiButton
          type='submit'
          form={formId}
          loading={isSubmitting}
          disabled={disabled}
          disabledReason={disabledReason}
          aria-describedby={ariaDescribedBy}
          className={className}
          variant={variant}
          size={size}
          {...(endIcon ? { endIcon } : { startIcon })}
          // Focus moving to the button is the mousedown default; Enter and Space never move it.
          onMouseDown={keepsFocus ? (event) => event.preventDefault() : undefined}
        >
          {label}
        </NoddiButton>
      )}
    </form.Subscribe>
  );
};
